import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import { toFriendly as toFriendlyMessageStatus } from 'constants/messageStatuses';
import { isMessageSmbReply, isAttachmentAllowed } from 'util/messageUtil';
import { OUTBOUND } from 'constants/messageDirection';
import { getMessageAvatar } from 'util/avatarUtil';
import * as directions from 'constants/messageDirection';
import { SenderInDisplayComponent } from 'components/Widgets/SenderInDisplayComponent/SenderInDisplayComponent';
import MessageTimeMarker from '../MessageTimeMarker/MessageTimeMarker';
import MessageImages from '../MessageImages/MessageImages';
import TranslationInfo from 'components/Messages/TranslationInfo/TranslationInfo';
import { AvatarBox, Avatar } from 'components/common/avatar';
import {
  Wrapper,
  WhiteSpaceNormalizeWrapper,
  InlineBlockComponent,
  ChatMessageBox,
  ChatMessage,
  ChatContent,
  ChatSentStatusMarker,
  ChatMessageHeader,
  SMBReplyMessageBody,
  LinkIcon,
  CustomerLocation,
  AttachmentWrapper,
  MainChat,
  InspectorButton,
} from './ChatMessage.styles';
import { isParsedJson } from '../../../util';
import MessageAttachment from '../MessageAttachment/MessageAttachment';
import { getFlowDebugModeStatus } from 'extractFromState';
import FlowDebugger from 'components/common/FlowDebugger/FlowDebugger';

const ChatMessageComponent = ({
  message,
  messageBodyContent,
  messageClasses,
  parentTicketId,
  isAdmin,
  messageStatusColor,
  messageTime,
  messageOriginalTime,
  messageTimeDiffExists,
  simplrAccountId,
  language,
  translatedFrom,
}) => {
  const isCustomerMessage = message.direction === directions.INBOUND;
  const addBorder = isCustomerMessage ? true : false;

  const isFlowDebugStateEnabled = useSelector(getFlowDebugModeStatus);
  const flowDebugState = message?.flowDebugState ?? {};
  const shouldRenderDebugState = isFlowDebugStateEnabled && !isEmpty(flowDebugState);

  const [isInspectorOpen, setIsInspectorOpen] = useState(false);

  let pageLinkedToConversation;

  let attachments;
  if (message.mediaUrl && isParsedJson(message.mediaUrl) && isAttachmentAllowed(message)) {
    attachments = message.mediaUrl.map((url) => {
      return <MessageAttachment src={url} key={url} />;
    });
  }

  if (message.isFirstInboundMessage && message.lastPageUrl) {
    pageLinkedToConversation = (
      <CustomerLocation
        onClick={() => {
          window.open(message.lastPageUrl, '_blank');
        }}
      >
        <LinkIcon src="/images/customer-location-icon.svg" />
        <p>Page customer is chatting from</p>
      </CustomerLocation>
    );
  }

  return (
    <Wrapper isCustomerMessage={isCustomerMessage} key={message.messageId}>
      <ChatMessage isCustomerMessage={isCustomerMessage}>
        <ChatMessageHeader isCustomerMessage={isCustomerMessage}>
          <InlineBlockComponent>
            <SenderInDisplayComponent isAdmin={isAdmin} message={message} isLiveChat={true} />
            <MessageTimeMarker
              isAdmin={isAdmin}
              messageDirection={message.direction}
              messageReceivedTime={messageTime}
              messageCreatedTime={messageOriginalTime}
              messageTimeDiffExists={messageTimeDiffExists}
            />
          </InlineBlockComponent>
          <InlineBlockComponent>
            {message.direction === OUTBOUND && !isMessageSmbReply(message) && (
              <ChatSentStatusMarker color={messageStatusColor}>
                {toFriendlyMessageStatus(message.status)}
              </ChatSentStatusMarker>
            )}
            {isMessageSmbReply(message) && (
              <SMBReplyMessageBody isLiveChat={true}>
                NOTE: This ticket is still open. Please respond.
              </SMBReplyMessageBody>
            )}
          </InlineBlockComponent>
        </ChatMessageHeader>
        <ChatContent isCustomerMessage={isCustomerMessage}>
          <AvatarBox>
            <Avatar alt="message" src={getMessageAvatar(message, parentTicketId)} />
          </AvatarBox>
          <MainChat>
            {!isEmpty(messageBodyContent) && (
              <>
                <ChatMessageBox
                  isCustomerMessage={isCustomerMessage}
                  isFlowDebugStateEnabled={shouldRenderDebugState}
                  className={messageClasses.join(' ')}
                  addBorder={addBorder}
                >
                  <WhiteSpaceNormalizeWrapper>{messageBodyContent}</WhiteSpaceNormalizeWrapper>
                  {translatedFrom && <TranslationInfo from={translatedFrom} to={language} />}
                  {shouldRenderDebugState && (
                    <InspectorButton
                      isInspectorVisible={!isCustomerMessage}
                      onClick={() => setIsInspectorOpen(!isInspectorOpen)}
                    ></InspectorButton>
                  )}
                </ChatMessageBox>
                {shouldRenderDebugState && (
                  <FlowDebugger
                    isInspectorOpen={isInspectorOpen}
                    isCustomerMessage={isCustomerMessage}
                    messageClasses={messageClasses}
                    flowDebugState={flowDebugState}
                  />
                )}
              </>
            )}
            {!isEmpty(attachments) && (
              <AttachmentWrapper addTopPadding={!isEmpty(messageBodyContent)}>{attachments}</AttachmentWrapper>
            )}
            <MessageImages
              message={message}
              isAdmin={isAdmin}
              avatarUrl={getMessageAvatar(message, parentTicketId)}
              messageOriginalTime={messageOriginalTime}
              simplrAccountId={simplrAccountId}
            />
          </MainChat>
        </ChatContent>
      </ChatMessage>
      {pageLinkedToConversation}
    </Wrapper>
  );
};

export default ChatMessageComponent;

ChatMessageComponent.propTypes = {
  isAdmin: PropTypes.bool.isRequired,
  message: PropTypes.object.isRequired,
  messageClasses: PropTypes.array.isRequired,
  messageStatusColor: PropTypes.string.isRequired,
  messageTime: PropTypes.string.isRequired,
  parentTicketId: PropTypes.string.isRequired,
  simplrAccountId: PropTypes.string.isRequired,
};
